import { useMemo, useState, type ReactNode } from 'react'
import {
  BadgeStatus,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  useToast,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { ItemPertanggungjawaban, Pertanggungjawaban } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiAkun, useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

export default function PertanggungjawabanPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const daftar = usePaged<Pertanggungjawaban>('/accountability', { query: {} })
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [detail, setDetail] = useState<Pertanggungjawaban | null>(null)

  const kolom = useMemo<KolomTabel<Pertanggungjawaban>[]>(
    () => [
      {
        kunci: 'reportNumber',
        judul: 'Nomor',
        render: (b) => (
          <button type="button" className="font-medium text-yayasan-800 hover:underline" onClick={() => setDetail(b)}>
            {b.reportNumber}
          </button>
        ),
      },
      { kunci: 'unit', judul: 'Unit', render: (b) => b.unit?.name ?? '—' },
      { kunci: 'periodNumber', judul: 'Periode', render: (b) => `Ke-${b.periodNumber}` },
      { kunci: 'totalReceived', judul: 'Diterima', rata: 'kanan', render: (b) => formatRupiah(b.totalReceived) },
      { kunci: 'totalSpent', judul: 'Dibelanjakan', rata: 'kanan', render: (b) => formatRupiah(b.totalSpent) },
      { kunci: 'remainingCash', judul: 'Sisa Kas', rata: 'kanan', render: (b) => formatRupiah(b.remainingCash) },
      { kunci: 'returnedAmount', judul: 'Dikembalikan', rata: 'kanan', render: (b) => formatRupiah(b.returnedAmount) },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
            Detail & Bukti
          </Button>
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <PageHeader
        judul="Pertanggungjawaban"
        deskripsi="Laporan penggunaan dana kas kecil/pencairan beserta bukti pengeluaran."
        ekspor={{
          path: '/reports/accountability',
          namaDasar: 'pertanggungjawaban',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada laporan pertanggungjawaban."
      />

      <DetailLaporan data={detail} onTutup={() => setDetail(null)} setelah={daftar.muat} punyaIzin={punyaIzin} />
    </div>
  )
}

function DetailLaporan({
  data,
  onTutup,
  setelah,
  punyaIzin,
}: {
  data: Pertanggungjawaban | null
  onTutup: () => void
  setelah: () => void
  punyaIzin: (...kode: string[]) => boolean
}) {
  const toast = useToast()
  const aksi = useAksi()
  const beban = useOpsiAkun({ hanyaPostable: true, tipe: 'EXPENSE' })
  const detail = useApi<Pertanggungjawaban>(data ? `/accountability/${data.id}` : null)
  const isi = detail.data ?? data

  const [modalItem, setModalItem] = useState(false)
  const [konfirmasi, setKonfirmasi] = useState<{ tindakan: 'submit' | 'review' | 'approve'; } | null>(null)
  const [unggahId, setUnggahId] = useState<string | null>(null)

  const kolomItem: KolomTabel<ItemPertanggungjawaban>[] = [
    { kunci: 'expenseDate', judul: 'Tanggal', render: (b) => formatTanggal(b.expenseDate) },
    { kunci: 'category', judul: 'Kategori', render: (b) => b.category ?? '—' },
    { kunci: 'description', judul: 'Uraian' },
    { kunci: 'amount', judul: 'Jumlah', rata: 'kanan', render: (b) => formatRupiah(b.amount) },
    { kunci: 'reviewStatus', judul: 'Review', render: (b) => <BadgeStatus status={b.reviewStatus} /> },
    {
      kunci: 'bukti',
      judul: 'Bukti',
      render: (b) =>
        b.evidenceAttachmentId ? (
          <a
            className="text-xs font-medium text-yayasan-800 hover:underline"
            href={`/api/attachments/${b.evidenceAttachmentId}/download`}
            target="_blank"
            rel="noreferrer"
          >
            Lihat Bukti
          </a>
        ) : (
          <span className="text-xs text-netral-400">Belum ada</span>
        ),
    },
    {
      kunci: 'aksi',
      judul: 'Unggah Bukti',
      sortable: false,
      render: (b) => (
        <label className="cursor-pointer text-xs font-medium text-yayasan-800 hover:underline">
          {unggahId === b.id ? 'Mengunggah…' : 'Pilih Berkas'}
          <input
            type="file"
            className="hidden"
            accept="image/*,application/pdf"
            onChange={async (e) => {
              const berkas = e.target.files?.[0]
              if (!berkas || !isi) return
              setUnggahId(b.id)
              try {
                const fd = new FormData()
                fd.append('file', berkas)
                fd.append('entityType', 'ACCOUNTABILITY_ITEM')
                fd.append('entityId', b.id)
                fd.append('description', `Bukti ${b.description}`)
                await api.unggah('/attachments', fd)
                toast.sukses('Bukti berhasil diunggah.')
                detail.muat()
              } catch (err) {
                toast.gagal(pesanKesalahan(err))
              } finally {
                setUnggahId(null)
              }
            }}
          />
        </label>
      ),
    },
  ]

  const jalankan = async (tindakan: string) => {
    if (!isi) return
    const hasil = await aksi.jalankan(() => api.post(`/accountability/${isi.id}/${tindakan}`, {}), {
      sukses: `Laporan berhasil di-${tindakan}.`,
    })
    if (hasil !== null) {
      setKonfirmasi(null)
      detail.muat()
      setelah()
    }
  }

  return (
    <>
      <Modal
        terbuka={data !== null}
        onTutup={onTutup}
        judul={isi ? `Laporan ${isi.reportNumber}` : 'Detail Laporan'}
        deskripsi="Rincian item pengeluaran, status review, dan unggah bukti."
        lebar="max-w-5xl"
        footer={
          <>
            {punyaIzin('accountability:create') ? (
              <Button varian="garis" onClick={() => setModalItem(true)}>
                + Tambah Item
              </Button>
            ) : null}
            {punyaIzin('accountability:submit') && isi?.status === 'DRAFT' ? (
              <Button onClick={() => setKonfirmasi({ tindakan: 'submit' })}>Ajukan</Button>
            ) : null}
            {punyaIzin('accountability:review') && isi?.status === 'SUBMITTED' ? (
              <Button varian="garis" onClick={() => setKonfirmasi({ tindakan: 'review' })}>
                Tinjau
              </Button>
            ) : null}
            {punyaIzin('accountability:approve') && isi?.status === 'REVIEWED' ? (
              <Button varian="sekunder" onClick={() => setKonfirmasi({ tindakan: 'approve' })}>
                Setujui
              </Button>
            ) : null}
            <Button varian="halus" onClick={onTutup}>
              Tutup
            </Button>
          </>
        }
      >
        {detail.galat ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{detail.galat}</p>
        ) : null}
        {isi ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Info label="Status">
                <BadgeStatus status={isi.status} />
              </Info>
              <Info label="Unit">{isi.unit?.name ?? '—'}</Info>
              <Info label="Periode">{`Ke-${isi.periodNumber}`}</Info>
              <Info label="Diterima">{formatRupiah(isi.totalReceived)}</Info>
              <Info label="Dibelanjakan">{formatRupiah(isi.totalSpent)}</Info>
              <Info label="Sisa Kas">{formatRupiah(isi.remainingCash)}</Info>
              <Info label="Dikembalikan">{formatRupiah(isi.returnedAmount)}</Info>
              <Info label="Jatuh Tempo">{formatTanggal(isi.dueDate ?? null)}</Info>
              <Info label="Periode Laporan">
                {formatTanggal(isi.periodStart ?? null)} — {formatTanggal(isi.periodEnd ?? null)}
              </Info>
            </div>

            <Card padat className="overflow-hidden">
              <div className="border-b border-netral-200 px-3 py-2">
                <h3 className="text-sm font-semibold text-netral-800">Item Pengeluaran</h3>
              </div>
              <DataTable
                kolom={kolomItem}
                data={isi.items ?? []}
                kunciBaris={(b) => b.id}
                memuat={detail.memuat && (isi.items ?? []).length === 0}
                pesanKosong="Belum ada item pengeluaran pada laporan ini."
              />
            </Card>
          </div>
        ) : null}
      </Modal>

      <FormItem
        terbuka={modalItem}
        onTutup={() => setModalItem(false)}
        reportId={isi?.id ?? null}
        opsiAkun={beban.opsi}
        setelah={() => {
          detail.muat()
          setelah()
        }}
      />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={`Konfirmasi ${konfirmasi?.tindakan ?? ''}`}
        pesan={`Lanjutkan tindakan "${konfirmasi?.tindakan}" untuk laporan ${isi?.reportNumber}?`}
        labelKonfirmasi="Lanjutkan"
        berbahaya={false}
        memuat={aksi.memuat}
        onKonfirmasi={() => {
          if (konfirmasi) void jalankan(konfirmasi.tindakan)
        }}
      />
    </>
  )
}

function FormItem({
  terbuka,
  onTutup,
  reportId,
  opsiAkun,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  reportId: string | null
  opsiAkun: Array<{ value: string; label: string }>
  setelah: () => void
}) {
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10))
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [accountId, setAccountId] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const simpan = async () => {
    setGalat(null)
    if (!reportId) return setGalat('Laporan tidak diketahui.')
    if (!description.trim()) return setGalat('Uraian wajib diisi.')
    const nilai = Number(amount.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah harus lebih dari nol.')
    setMemuat(true)
    try {
      await api.post(`/accountability/${reportId}/items`, {
        expenseDate,
        category: category.trim() || undefined,
        description: description.trim(),
        amount: nilai,
        accountId: accountId || undefined,
      })
      setDescription('')
      setAmount('')
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Tambah Item Pengeluaran"
      lebar="max-w-xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan Item
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <KolomForm kolom={2}>
          <Input type="date" label="Tanggal Pengeluaran" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
          <Input label="Kategori" value={category} onChange={(e) => setCategory(e.target.value)} />
          <Input
            label="Jumlah (Rp)"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <Select
            label="Akun Beban"
            placeholder="Opsional"
            opsi={opsiAkun}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          />
        </KolomForm>
        <Textarea
          label="Uraian"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={2}
        />
      </div>
    </Modal>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm text-netral-800">{children}</p>
    </div>
  )
}
